import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Send, ArrowLeft, MapPin, Loader2, Wifi, WifiOff } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/shared";
import { listThreads, getMessages, sendMessage } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const FALLBACK_MS = 8000;

export default function Messages() {
  const { otherId } = useParams();
  const { currentUser, subscribe, socketConnected } = useApp();
  const navigate = useNavigate();
  const [threads, setThreads] = useState(null);

  const loadThreads = () => listThreads().then(setThreads).catch(() => {});
  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, socketConnected ? FALLBACK_MS * 3 : FALLBACK_MS);
    return () => clearInterval(id);
  }, [socketConnected]);

  useEffect(() => {
    const unsub = subscribe?.((ev) => {
      if (ev.type === "message" || ev.type === "message_sent") loadThreads();
    });
    return () => unsub?.();
  }, [subscribe]);

  return (
    <div className="min-h-screen grain">
      <Header title="Messages" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-primary">
          <MessageSquare className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">Direct messages</span>
          <span data-testid="socket-status" className={`ml-2 inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase ${socketConnected ? "text-primary" : "text-muted-foreground"}`}>
            {socketConnected ? <><Wifi className="w-3 h-3" /> Live</> : <><WifiOff className="w-3 h-3" /> Offline</>}
          </span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Messages</h1>

        <div className="mt-6 grid md:grid-cols-[320px_1fr] gap-4 rounded-lg border border-border bg-card overflow-hidden min-h-[560px]">
          <aside className={`border-r border-border ${otherId ? "hidden md:block" : ""}`} data-testid="thread-list">
            {threads === null ? (
              <div className="p-4 space-y-3">{[0,1,2].map((i)=><Skeleton key={i} className="h-14 rounded-xl" />)}</div>
            ) : threads.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">
                No conversations yet. Connect with someone first, then start a chat from the Network page.
              </div>
            ) : threads.map((t) => (
              <button
                key={t.other_user_id}
                data-testid={`thread-${t.other_user_id}`}
                onClick={() => navigate(`/messages/${t.other_user_id}`)}
                className={`w-full text-left p-3 flex items-center gap-3 border-b border-border hover:bg-accent transition-colors ${otherId === t.other_user_id ? "bg-accent" : ""}`}
              >
                <Avatar className="w-10 h-10">
                  <AvatarImage src={t.other?.avatar_url} className="object-cover" />
                  <AvatarFallback>{t.other?.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <div className="font-semibold truncate">{t.other?.name}</div>
                    {t.unread_count > 0 && <span className="ml-auto rounded-full bg-primary text-primary-foreground text-[10px] px-1.5 font-bold" data-testid={`thread-unread-${t.other_user_id}`}>{t.unread_count}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {t.last_message.from_user_id === currentUser.id ? "You: " : ""}{t.last_message.text}
                  </div>
                </div>
              </button>
            ))}
          </aside>
          <section className={`min-h-[560px] flex flex-col ${!otherId ? "hidden md:flex" : "flex"}`}>
            {otherId
              ? <ChatPane key={otherId} otherId={otherId} onReloadThreads={loadThreads} />
              : (
                <div className="flex-1 flex items-center justify-center text-center text-muted-foreground p-6">
                  <div>
                    <MessageSquare className="w-10 h-10 mx-auto opacity-40" />
                    <p className="mt-3">Pick a conversation to start chatting.</p>
                  </div>
                </div>
              )}
          </section>
        </div>
      </div>
    </div>
  );
}

const ChatPane = ({ otherId, onReloadThreads }) => {
  const { currentUser, subscribe, sendSocket, socketConnected } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastCountRef = useRef(0);

  const load = () => getMessages(otherId).then(setData).catch(() => {});
  useEffect(() => {
    load(); onReloadThreads?.();
    const id = setInterval(load, socketConnected ? FALLBACK_MS * 2 : FALLBACK_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [otherId, socketConnected]);

  useEffect(() => {
    const unsub = subscribe?.((ev) => {
      if (ev.type === "message" && ev.message?.from_user_id === otherId) {
        setData((d) => d ? { ...d, messages: [...d.messages, ev.message] } : d);
        // mark read via reload
        setTimeout(load, 200);
      }
      if (ev.type === "typing" && ev.from_user_id === otherId) {
        setOtherTyping(true);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setOtherTyping(false), 2500);
      }
    });
    return () => unsub?.();
    // eslint-disable-next-line
  }, [subscribe, otherId]);

  useEffect(() => {
    if (data && data.messages.length !== lastCountRef.current) {
      lastCountRef.current = data.messages.length;
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }));
    }
  }, [data]);

  const send = async () => {
    const t = text.trim(); if (!t) return;
    setSending(true);
    try {
      const msg = await sendMessage(otherId, t);
      setText("");
      setData((d) => d ? { ...d, messages: [...d.messages, msg] } : d);
      onReloadThreads?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Message failed");
    }
    setSending(false);
  };

  const onInput = (e) => {
    setText(e.target.value);
    if (socketConnected) sendSocket?.({ type: "typing", to: otherId });
  };

  if (!data) return <div className="p-6"><Skeleton className="h-8 rounded-xl" /><Skeleton className="h-40 rounded-xl mt-3" /></div>;

  return (
    <div className="flex flex-col h-full min-h-[560px]" data-testid="chat-pane">
      <div className="p-4 border-b border-border flex items-center gap-3">
        <button className="md:hidden" onClick={() => navigate("/messages")}><ArrowLeft className="w-5 h-5" /></button>
        <Avatar className="w-10 h-10">
          <AvatarImage src={data.other?.avatar_url} className="object-cover" />
          <AvatarFallback>{data.other?.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <button
            data-testid="chat-other-name"
            onClick={() => data.other?.role === "worker" && navigate(`/resume/${data.other.id}`)}
            className="font-heading font-bold hover:text-primary transition-colors"
          >
            {data.other?.name}
          </button>
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />{data.other?.location}
            {otherTyping && <span data-testid="typing-indicator" className="ml-2 text-primary font-semibold">typing…</span>}
          </div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {data.messages.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-10">Say hi to start the conversation.</p>
        )}
        {data.messages.map((m) => {
          const mine = m.from_user_id === currentUser.id;
          return (
            <motion.div key={m.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div data-testid={`msg-${m.id}`} className={`max-w-[80%] rounded-lg px-4 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-secondary-foreground rounded-bl-sm"}`}>
                {m.text}
                <div className={`text-[10px] mt-1 opacity-70 ${mine ? "text-primary-foreground" : "text-muted-foreground"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      {data.connected ? (
        <div className="p-3 border-t border-border flex gap-2">
          <Input data-testid="chat-input" value={text} onChange={onInput}
                 onKeyDown={(e) => e.key === "Enter" && !sending && send()}
                 placeholder="Type a message…" />
          <Button data-testid="chat-send-btn" onClick={send} disabled={sending || !text.trim()} size="icon">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      ) : (
        <div className="p-4 border-t border-border text-sm text-muted-foreground text-center">
          You need to be connected to message this user.
        </div>
      )}
    </div>
  );
};
